import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Undo,
  Redo,
  Trash2,
  Save,
  X,
  Square,
  Circle as CircleIcon,
  MoveUpRight,
  Pencil,
  Type,
  MousePointer2,
} from 'lucide-react';
import { Button } from './Button';
import { Slider } from './Slider';
import { Input } from './Input';
import { useCreateAnnotation } from '../helpers/useAnnotationsApi';
import { toast } from 'sonner';
import styles from './AnnotationCanvas.module.css';

// Types for our shapes
type Point = { x: number; y: number };

type ShapeBase = {
  id: string;
  color: string;
  strokeWidth: number;
};

type RectShape = ShapeBase & {
  type: 'rect';
  x: number;
  y: number;
  width: number;
  height: number;
};

type CircleShape = ShapeBase & {
  type: 'circle';
  x: number;
  y: number;
  radius: number;
};

type ArrowShape = ShapeBase & {
  type: 'arrow';
  startX: number;
  startY: number;
  endX: number;
  endY: number;
};

type FreehandShape = ShapeBase & {
  type: 'freehand';
  points: Point[];
};

type TextShape = ShapeBase & {
  type: 'text';
  x: number;
  y: number;
  text: string;
};

type Shape = RectShape | CircleShape | ArrowShape | FreehandShape | TextShape;

type Tool = 'select' | 'rect' | 'circle' | 'arrow' | 'freehand' | 'text';

const COLORS = [
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#3b82f6', // Blue
  '#a855f7', // Purple
  '#ffffff', // White
];

interface AnnotationCanvasProps {
  imageUrl?: string;
  sessionId: string;
  attachmentId?: string;
  checkpointId?: string;
  onSave?: (annotationData: any) => void;
  onCancel?: () => void;
  className?: string;
}

export const AnnotationCanvas: React.FC<AnnotationCanvasProps> = ({
  imageUrl,
  sessionId,
  attachmentId,
  checkpointId,
  onSave,
  onCancel,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [history, setHistory] = useState<Shape[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [currentTool, setCurrentTool] = useState<Tool>('freehand');
  const [currentColor, setCurrentColor] = useState(COLORS[0]);
  const [currentStrokeWidth, setCurrentStrokeWidth] = useState(3);
  const [description, setDescription] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [currentShape, setCurrentShape] = useState<Shape | null>(null);
  const [textInputPos, setTextInputPos] = useState<Point | null>(null);
  const [textInputValue, setTextInputValue] = useState('');
  const [isDraggingTextInput, setIsDraggingTextInput] = useState(false);
  const [textDragOffset, setTextDragOffset] = useState<Point | null>(null);
  const textInputWrapperRef = useRef<HTMLDivElement>(null);

  const createAnnotationMutation = useCreateAnnotation();

  // Initialize canvas size
  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current && canvasRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        canvasRef.current.width = width;
        canvasRef.current.height = height;
        redraw();
      }
    };

    window.addEventListener('resize', updateCanvasSize);
    updateCanvasSize();

    // If there's an image, we might want to wait for it to load to size correctly,
    // but for now we rely on the container sizing (which should be controlled by CSS/Image)
    const img = new Image();
    if (imageUrl) {
      img.src = imageUrl;
      img.onload = updateCanvasSize;
    }

    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [imageUrl, shapes, currentShape]);

  // Drawing loop
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const drawShape = (shape: Shape) => {
      ctx.strokeStyle = shape.color;
      ctx.lineWidth = shape.strokeWidth;
      ctx.fillStyle = shape.color;

      ctx.beginPath();
      if (shape.type === 'rect') {
        ctx.strokeRect(shape.x, shape.y, shape.width, shape.height);
      } else if (shape.type === 'circle') {
        ctx.arc(shape.x, shape.y, shape.radius, 0, 2 * Math.PI);
        ctx.stroke();
      } else if (shape.type === 'arrow') {
        // Draw line
        ctx.moveTo(shape.startX, shape.startY);
        ctx.lineTo(shape.endX, shape.endY);
        ctx.stroke();

        // Draw arrow head
        const headLength = 15 + shape.strokeWidth;
        const angle = Math.atan2(shape.endY - shape.startY, shape.endX - shape.startX);
        ctx.beginPath();
        ctx.moveTo(shape.endX, shape.endY);
        ctx.lineTo(
          shape.endX - headLength * Math.cos(angle - Math.PI / 6),
          shape.endY - headLength * Math.sin(angle - Math.PI / 6),
        );
        ctx.lineTo(
          shape.endX - headLength * Math.cos(angle + Math.PI / 6),
          shape.endY - headLength * Math.sin(angle + Math.PI / 6),
        );
        ctx.lineTo(shape.endX, shape.endY);
        ctx.fill();
      } else if (shape.type === 'freehand') {
        if (shape.points.length > 0) {
          ctx.moveTo(shape.points[0].x, shape.points[0].y);
          for (let i = 1; i < shape.points.length; i++) {
            ctx.lineTo(shape.points[i].x, shape.points[i].y);
          }
          ctx.stroke();
        }
      } else if (shape.type === 'text') {
        ctx.font = `${16 + shape.strokeWidth * 2}px sans-serif`;
        ctx.textBaseline = 'top';
        ctx.fillText(shape.text, shape.x, shape.y);
      }
    };

    shapes.forEach(drawShape);
    if (currentShape) drawShape(currentShape);
  }, [shapes, currentShape]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  const getMousePos = (e: React.MouseEvent | MouseEvent): Point => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (currentTool === 'select') return; // Selection not implemented yet
    if (currentTool === 'text') {
      const pos = getMousePos(e);
      setTextInputPos(pos);
      setTextInputValue('');
      // Focus will be handled by the input rendering
      return;
    }

    setIsDrawing(true);
    const pos = getMousePos(e);
    setStartPoint(pos);

    const base = {
      id: crypto.randomUUID(),
      color: currentColor,
      strokeWidth: currentStrokeWidth,
    };

    if (currentTool === 'freehand') {
      setCurrentShape({ ...base, type: 'freehand', points: [pos] });
    } else if (currentTool === 'rect') {
      setCurrentShape({ ...base, type: 'rect', x: pos.x, y: pos.y, width: 0, height: 0 });
    } else if (currentTool === 'circle') {
      setCurrentShape({ ...base, type: 'circle', x: pos.x, y: pos.y, radius: 0 });
    } else if (currentTool === 'arrow') {
      setCurrentShape({
        ...base,
        type: 'arrow',
        startX: pos.x,
        startY: pos.y,
        endX: pos.x,
        endY: pos.y,
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing || !startPoint || !currentShape) return;
    const pos = getMousePos(e);

    if (currentShape.type === 'freehand') {
      setCurrentShape({
        ...currentShape,
        points: [...currentShape.points, pos],
      });
    } else if (currentShape.type === 'rect') {
      setCurrentShape({
        ...currentShape,
        width: pos.x - startPoint.x,
        height: pos.y - startPoint.y,
      });
    } else if (currentShape.type === 'circle') {
      const radius = Math.sqrt(
        Math.pow(pos.x - startPoint.x, 2) + Math.pow(pos.y - startPoint.y, 2),
      );
      setCurrentShape({ ...currentShape, radius });
    } else if (currentShape.type === 'arrow') {
      setCurrentShape({ ...currentShape, endX: pos.x, endY: pos.y });
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing || !currentShape) return;
    setIsDrawing(false);
    addShape(currentShape);
    setCurrentShape(null);
    setStartPoint(null);
  };

  const addShape = (shape: Shape) => {
    const newShapes = [...shapes, shape];
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newShapes);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setShapes(newShapes);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setShapes(history[historyIndex - 1]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setShapes(history[historyIndex + 1]);
    }
  };

  const handleClear = () => {
    const newShapes: Shape[] = [];
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newShapes);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setShapes(newShapes);
  };

  const handleTextSubmit = () => {
    if (textInputPos && textInputValue.trim()) {
      addShape({
        id: crypto.randomUUID(),
        type: 'text',
        x: textInputPos.x,
        y: textInputPos.y,
        text: textInputValue,
        color: currentColor,
        strokeWidth: currentStrokeWidth,
      });
    }
    setTextInputPos(null);
    setTextInputValue('');
    setIsDraggingTextInput(false);
    setTextDragOffset(null);
  };

  const handleTextDragStart = (e: React.MouseEvent) => {
    if (!textInputPos || !canvasRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingTextInput(true);
    setTextDragOffset({
      x: e.clientX - textInputPos.x,
      y: e.clientY - textInputPos.y,
    });
  };

  // Drag floating text input by its handle.
  useEffect(() => {
    if (!isDraggingTextInput || !textDragOffset || !canvasRef.current) return;

    const onMouseMove = (event: MouseEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const nextX = event.clientX - textDragOffset.x;
      const nextY = event.clientY - textDragOffset.y;
      const clampedX = Math.max(8, Math.min(nextX, rect.width - 180));
      const clampedY = Math.max(8, Math.min(nextY, rect.height - 44));
      setTextInputPos({ x: clampedX, y: clampedY });
    };

    const onMouseUp = () => {
      setIsDraggingTextInput(false);
      setTextDragOffset(null);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDraggingTextInput, textDragOffset]);

  // Commit text when clicking outside the floating input.
  useEffect(() => {
    if (!textInputPos) return;

    const handleOutsidePointer = (event: MouseEvent) => {
      if (isDraggingTextInput) return;
      const wrapper = textInputWrapperRef.current;
      if (!wrapper) return;
      const target = event.target as Node | null;
      if (target && wrapper.contains(target)) return;
      handleTextSubmit();
    };

    window.addEventListener('mousedown', handleOutsidePointer, true);
    return () => {
      window.removeEventListener('mousedown', handleOutsidePointer, true);
    };
  }, [textInputPos, textInputValue, isDraggingTextInput, currentColor, currentStrokeWidth]);

  const handleSave = async () => {
    // Transform internal shapes to API compatible shapes (using points array)
    const apiShapes = shapes
      .filter((s) => s.type !== 'text')
      .map((s) => {
        const base = {
          color: s.color,
          strokeWidth: s.strokeWidth,
        };

        if (s.type === 'rect') {
          return {
            ...base,
            type: 'rect' as const,
            points: [
              { x: s.x, y: s.y },
              { x: s.x + s.width, y: s.y + s.height },
            ],
          };
        }
        if (s.type === 'circle') {
          return {
            ...base,
            type: 'circle' as const,
            points: [
              { x: s.x, y: s.y },
              { x: s.radius, y: 0 },
            ],
          };
        }
        if (s.type === 'arrow') {
          return {
            ...base,
            type: 'arrow' as const,
            points: [
              { x: s.startX, y: s.startY },
              { x: s.endX, y: s.endY },
            ],
          };
        }
        if (s.type === 'freehand') {
          return {
            ...base,
            type: 'freehand' as const,
            points: s.points,
          };
        }
        return null;
      })
      .filter((s): s is NonNullable<typeof s> => s !== null);

    const annotationData = {
      shapes: apiShapes,
      textNotes: shapes
        .filter((s) => s.type === 'text')
        .map((s) => ({
          x: (s as TextShape).x,
          y: (s as TextShape).y,
          text: (s as TextShape).text,
        })),
    };

    try {
      // Capture an image compositing the background image with the annotation canvas
      let annotatedImage: string | null = null;
      try {
        if (canvasRef.current) {
          const canvas = canvasRef.current;
          const { width, height } = canvas;

          // Create a new canvas to composite the background image + annotations
          const compositeCanvas = document.createElement('canvas');
          compositeCanvas.width = width;
          compositeCanvas.height = height;
          const ctx = compositeCanvas.getContext('2d', {
            alpha: true,
            willReadFrequently: false,
          });

          if (ctx) {
            // Enable high-quality rendering
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            // First, draw the background image if available
            if (imageUrl) {
              const bgImg = new Image();
              bgImg.crossOrigin = 'anonymous';
              await new Promise<void>((resolve) => {
                bgImg.onload = () => {
                  // Draw background image scaled to fit canvas
                  ctx.drawImage(bgImg, 0, 0, width, height);
                  resolve();
                };
                bgImg.onerror = () => {
                  // If background fails to load, fill with dark background
                  ctx.fillStyle = '#1a1a2e';
                  ctx.fillRect(0, 0, width, height);
                  resolve();
                };
                bgImg.src = imageUrl;
              });
            } else {
              // No background image, fill with dark background
              ctx.fillStyle = '#1a1a2e';
              ctx.fillRect(0, 0, width, height);
            }

            // Redraw all shapes directly on composite canvas with proper colors
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';

            shapes.forEach((shape) => {
              ctx.strokeStyle = shape.color;
              ctx.lineWidth = shape.strokeWidth;
              ctx.fillStyle = shape.color;

              ctx.beginPath();
              if (shape.type === 'rect') {
                ctx.strokeRect(shape.x, shape.y, shape.width, shape.height);
              } else if (shape.type === 'circle') {
                ctx.arc(shape.x, shape.y, shape.radius, 0, 2 * Math.PI);
                ctx.stroke();
              } else if (shape.type === 'arrow') {
                // Draw line
                ctx.moveTo(shape.startX, shape.startY);
                ctx.lineTo(shape.endX, shape.endY);
                ctx.stroke();

                // Draw arrow head
                const headLength = 15 + shape.strokeWidth;
                const angle = Math.atan2(shape.endY - shape.startY, shape.endX - shape.startX);
                ctx.beginPath();
                ctx.moveTo(shape.endX, shape.endY);
                ctx.lineTo(
                  shape.endX - headLength * Math.cos(angle - Math.PI / 6),
                  shape.endY - headLength * Math.sin(angle - Math.PI / 6),
                );
                ctx.lineTo(
                  shape.endX - headLength * Math.cos(angle + Math.PI / 6),
                  shape.endY - headLength * Math.sin(angle + Math.PI / 6),
                );
                ctx.lineTo(shape.endX, shape.endY);
                ctx.fill();
              } else if (shape.type === 'freehand') {
                if (shape.points.length > 0) {
                  ctx.moveTo(shape.points[0].x, shape.points[0].y);
                  for (let i = 1; i < shape.points.length; i++) {
                    ctx.lineTo(shape.points[i].x, shape.points[i].y);
                  }
                  ctx.stroke();
                }
              } else if (shape.type === 'text') {
                ctx.font = `${16 + shape.strokeWidth * 2}px sans-serif`;
                ctx.textBaseline = 'top';
                ctx.fillText(shape.text, shape.x, shape.y);
              }
            });

            // High quality PNG export
            annotatedImage = compositeCanvas.toDataURL('image/png', 1.0);
          }
        }
      } catch (e) {
        // ignore capture errors
        console.error('Failed to capture annotated image:', e);
        annotatedImage = null;
      }

      await createAnnotationMutation.mutateAsync({
        sessionId,
        attachmentId,
        checkpointId,
        description,
        annotationData,
      });
      if (onSave) onSave({ annotationData, annotatedImage, description });
    } catch (error) {
      // Error handling is done in the mutation hook via toast
    }
  };

  return (
    <div className={`${styles.container} ${className || ''}`} ref={containerRef}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolGroup}>
          <Button
            size="icon-sm"
            variant={currentTool === 'freehand' ? 'primary' : 'ghost'}
            onClick={() => setCurrentTool('freehand')}
            title="Freehand"
          >
            <Pencil size={16} />
          </Button>
          <Button
            size="icon-sm"
            variant={currentTool === 'arrow' ? 'primary' : 'ghost'}
            onClick={() => setCurrentTool('arrow')}
            title="Arrow"
          >
            <MoveUpRight size={16} />
          </Button>
          <Button
            size="icon-sm"
            variant={currentTool === 'rect' ? 'primary' : 'ghost'}
            onClick={() => setCurrentTool('rect')}
            title="Rectangle"
          >
            <Square size={16} />
          </Button>
          <Button
            size="icon-sm"
            variant={currentTool === 'circle' ? 'primary' : 'ghost'}
            onClick={() => setCurrentTool('circle')}
            title="Circle"
          >
            <CircleIcon size={16} />
          </Button>
          <Button
            size="icon-sm"
            variant={currentTool === 'text' ? 'primary' : 'ghost'}
            onClick={() => setCurrentTool('text')}
            title="Text"
          >
            <Type size={16} />
          </Button>
        </div>

        <div className={styles.separator} />

        <div className={styles.colorGroup}>
          {COLORS.map((color) => (
            <button
              key={color}
              className={`${styles.colorBtn} ${currentColor === color ? styles.activeColor : ''}`}
              style={{ backgroundColor: color }}
              onClick={() => setCurrentColor(color)}
              aria-label={`Select color ${color}`}
            />
          ))}
        </div>

        <div className={styles.separator} />

        <div className={styles.sliderGroup}>
          <div
            className={styles.strokePreview}
            style={{ height: currentStrokeWidth, backgroundColor: currentColor }}
          />
          <Slider
            value={[currentStrokeWidth]}
            min={1}
            max={10}
            step={1}
            onValueChange={(val) => setCurrentStrokeWidth(val[0])}
            className={styles.slider}
          />
        </div>

        <div className={styles.separator} />

        <div className={styles.actionGroup}>
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            title="Undo"
          >
            <Undo size={16} />
          </Button>
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            title="Redo"
          >
            <Redo size={16} />
          </Button>
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={handleClear}
            title="Clear All"
            className={styles.dangerBtn}
          >
            <Trash2 size={16} />
          </Button>
        </div>
      </div>

      {/* Canvas Area */}
      <div className={styles.canvasWrapper}>
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Annotation background"
            className={styles.bgImage}
            onError={() => {
              toast.error('Failed to load annotation image — try re-capturing the screenshot');
            }}
          />
        )}
        <canvas
          ref={canvasRef}
          className={styles.canvas}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        />

        {/* Text Input Overlay */}
        {textInputPos && (
          <div
            ref={textInputWrapperRef}
            className={styles.textInputWrapper}
            style={{
              left: `${textInputPos.x}px`,
              top: `${textInputPos.y}px`,
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseMove={(e) => e.stopPropagation()}
          >
            <div className={styles.textInputToolbar}>
              <button
                type="button"
                className={styles.textDragHandle}
                onMouseDown={handleTextDragStart}
                title="Drag text box"
                aria-label="Drag text box"
              >
                <MousePointer2 size={12} />
              </button>
              <button
                type="button"
                className={styles.textConfirmBtn}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleTextSubmit();
                }}
              >
                Add
              </button>
            </div>
            <input
              id="annotation-text-input"
              name="annotation-text-input"
              autoFocus
              type="text"
              className={styles.floatingInput}
              value={textInputValue}
              onChange={(e) => setTextInputValue(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleTextSubmit();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setTextInputPos(null);
                  setTextInputValue('');
                }
              }}
              placeholder="Type text..."
              style={{
                color: currentColor,
                fontSize: `${16 + currentStrokeWidth * 2}px`,
                fontWeight: 600,
              }}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <Input
          placeholder="Add a description for this annotation..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className={styles.descriptionInput}
        />
        <div className={styles.footerActions}>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={createAnnotationMutation.isPending || (shapes.length === 0 && !description)}
          >
            {createAnnotationMutation.isPending ? 'Saving...' : 'Save Annotation'}
          </Button>
        </div>
      </div>
    </div>
  );
};
